import React, { useState } from 'react';
import { Loader2, Upload, Book, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useFlashcards } from '../../hooks/useFlashcards';

const CreateFlashCard = ({ course, refetch }) => {
  const [activeTab, setActiveTab] = useState('document');
  const [topic, setTopic] = useState('');
  const [ytLink, setYtLink] = useState('');
  const [files, setFiles] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  
  const { createFlashcards } = useFlashcards(course._id);
  const createFlashcardMutation = createFlashcards;
  
  const validateForm = () => {
    if (activeTab === 'topic' && !topic) {
      setError('Please enter a topic.');
      return false;
    }
    if (
      activeTab === 'youtube' &&
      (!ytLink ||
        (!ytLink.includes('youtube.com') && !ytLink.includes('youtu.be')))
    ) {
      setError('Please enter a valid YouTube URL.');
      return false;
    }
    if (activeTab === 'document' && !files) {
      setError('Please upload a document.');
      return false;
    }
    return true;
  };

  const resetForm = () => {
    setTopic('');
    setYtLink('');
    setFiles(null);
  };

  const createQnA = async () => {
    if (!validateForm()) return;

    try {
      setError('');
      setSuccess(false);

      const formData = new FormData();
      formData.append('topic', topic || '');
      formData.append('userId', course?.userId);
      formData.append('title', course?.title);
      formData.append('input_type', activeTab === 'youtube' ? 'yt_link' : activeTab === 'document' ? 'doc' : 'topics');
      formData.append('output_type', 'flashcard');

      if (ytLink) formData.append('yt_link', ytLink);
      if (files) formData.append('files', files);

      const config = {};

   

      await createFlashcardMutation.mutateAsync({ requestData: formData, config });

      refetch();
      setSuccess(true);
      resetForm();
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to create flash card. Please try again.';
      setError(errorMsg);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size > 10 * 1024 * 1024) {
      setError('File size should be less than 10MB.');
      return;
    }
    setFiles(selectedFile);
    setTopic('');
    setYtLink('');
  };

  return (
    <div className="flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-lg rounded-2xl bg-muted/40">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Create Flash Card
          </CardTitle>
          <CardDescription>
            Generate flash cards from topics, YouTube videos, or documents.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert className="mb-4 bg-green-50 text-green-800 border-green-200">
              <AlertDescription>
                Flash card created successfully!
              </AlertDescription>
            </Alert>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid grid-cols-3 mb-4">
              <TabsTrigger disabled value="youtube" className="flex items-center gap-2">
                <Youtube className="w-4 h-4" />
                YouTube
              </TabsTrigger>
              <TabsTrigger value="document" className="flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Document
              </TabsTrigger>
              <TabsTrigger value="topic" className="flex items-center gap-2">
                <Book className="w-4 h-4" />
                Topic
              </TabsTrigger>
            </TabsList>

            <TabsContent value="topic">
              <Input
                id="topic-input"
                type="text"
                value={topic}
                onChange={(e) => {
                  setTopic(e.target.value);
                  setYtLink('');
                  setFiles(null);
                }}
                placeholder="Enter the topic for your flash cards"
              />
            </TabsContent>

            <TabsContent value="youtube">
              <Input
                type="text"
                value={ytLink}
                onChange={(e) => {
                  setYtLink(e.target.value);
                  setFiles(null);
                  setTopic('');
                }}
                placeholder="Paste YouTube video URL"
              />
            </TabsContent>

            <TabsContent value="document">
              <Input type="file" onChange={handleFileChange} accept=".pdf,.txt,.md" />
              {files && <p className="text-sm mt-2 text-center">{files.name}</p>}
            </TabsContent>
          </Tabs>

          <Button
            onClick={createQnA}
            disabled={createFlashcardMutation.isPending}
            className="w-full h-12 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg transition-all transform hover:scale-[1.02] disabled:opacity-50"
          >
            {createFlashcardMutation.isPending ? (
              <div className="flex items-center justify-center space-x-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Creating...</span>
              </div>
            ) : (
              <span>Create Flash Card ✨</span>
            )}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateFlashCard;
